import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Copy, Check, ArrowLeft, Download, Play, Tag } from "lucide-react";
import { supabase } from "../lib/supabase";
import SEO, { generateHowToSchema, generateBreadcrumbSchema } from "./SEO";
import { Navigation } from "./Navigation";
import ContactModal from "./ContactModal";
import EmailCaptureModal from "./EmailCaptureModal";
import YouTubeEmbed from "./YouTubeEmbed";
import Footer from "./Footer";

interface Prompt {
  id: string;
  title: string;
  description: string;
  content: string;
  category_id?: string;
  prompt_categories?: {
    id: string;
    name: string;
    slug: string;
  };
  prompt_tags_map?: Array<{
    prompt_tags: {
      id: string;
      name: string;
      slug: string;
    };
  }>;
  is_featured: boolean;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  deleted: boolean;
  published_at?: string | null;
  download_url?: string;
  download_button_text?: string;
  youtube_video_id?: string;
}

function generateSlug(title: string): string {
  const wordsToRemove = [
    "a",
    "an",
    "the",
    "and",
    "or",
    "but",
    "in",
    "on",
    "at",
    "to",
    "for",
    "of",
    "with",
    "by",
  ];
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .split(/\s+/)
    .filter((word) => !wordsToRemove.includes(word))
    .join("-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/^blog\//, "");
}

export default function PromptDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  useEffect(() => {
    const fetchPrompt = async () => {
      if (!slug) {
        setError("No prompt slug provided");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        // Fetch all published, non-deleted prompts
        const { data: prompts, error: fetchError } = await supabase
          .from("prompts")
          .select(
            `*,
            prompt_categories (
              id,
              name,
              slug
            ),
            prompt_tags_map (
              prompt_tags (
                id,
                name,
                slug
              )
            )`,
          )
          .eq("is_published", true)
          .eq("deleted", false);
        if (fetchError || !prompts) {
          setError("Prompt not found");
          setLoading(false);
          return;
        }
        // Find the prompt with a matching slug
        console.log("Looking for slug:", slug);
        console.log(
          "Available prompts:",
          prompts.map((p: any) => ({
            title: p.title,
            generatedSlug: generateSlug(p.title),
          })),
        );
        const matchingPrompt = prompts.find((p: any) => {
          const generatedSlug = generateSlug(p.title);
          const matches = generatedSlug === slug || p.id === slug;
          console.log(
            `Checking prompt "${p.title}": generated slug "${generatedSlug}" matches "${slug}"? ${matches}`,
          );
          return matches;
        });
        if (!matchingPrompt) {
          setError("Prompt not found");
          setLoading(false);
          return;
        }
        setPrompt(matchingPrompt);
      } catch (err) {
        console.error("Error fetching prompt:", err);
        setError("Failed to load prompt");
      } finally {
        setLoading(false);
      }
    };
    fetchPrompt();
  }, [slug]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleDownload = () => {
    if (prompt?.download_url) {
      setShowDownloadModal(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="h-64 bg-gray-200 rounded mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !prompt) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Prompt Not Found
            </h1>
            <p className="text-gray-600 mb-8">
              The prompt you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate("/prompt-library")}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Prompt Library
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Generate schema markup for the individual prompt
  const promptSchema = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: prompt.title,
    description: prompt.description,
    text: prompt.content,
    url: `https://one80labs.com/prompt/${slug}`,
    author: {
      "@type": "Organization",
      name: "One80Labs",
      url: "https://one80labs.com",
    },
    publisher: {
      "@type": "Organization",
      name: "One80Labs",
      url: "https://one80labs.com",
    },
    datePublished: prompt.published_at || prompt.created_at,
    dateModified: prompt.updated_at,
    isAccessibleForFree: "True",
    genre: "AI Prompt",
    keywords:
      (prompt as any).prompt_tags_map
        ?.map((tag: any) => tag.prompt_tags.name)
        .join(", ") || "",
    about: [
      {
        "@type": "Thing",
        name: "AI Prompt Engineering",
        description: "Techniques for creating effective prompts for AI tools",
      },
      {
        "@type": "Thing",
        name: "ChatGPT",
        description: "AI language model for text generation and conversation",
      },
      {
        "@type": "Thing",
        name: "Gemini",
        description: "Google's AI model for text and multimodal tasks",
      },
    ],
    audience: {
      "@type": "Audience",
      audienceType:
        "Business professionals, content creators, AI practitioners",
      geographicArea: {
        "@type": "Place",
        name: "Omaha, Nebraska, Midwest",
      },
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs - AI Prompt Engineering in Omaha",
    description:
      "The Midwest's Premier AI Agency providing prompt engineering expertise and AI training in Omaha, Nebraska.",
    url: "https://one80labs.com",
    telephone: "+1-402-915-3613",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
      },
      {
        "@type": "City",
        name: "Lincoln",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${prompt.title} - AI Prompt | One80Labs`}
        description={prompt.description}
        image="https://images.unsplash.com/photo-1677442135968-6d89485dd1d1"
        jsonLd={[
          generateHowToSchema({
            name: prompt.title,
            description: prompt.description,
            image:
              "https://images.unsplash.com/photo-1677442135968-6d89485dd1d1",
            url: `https://one80labs.com/prompt/${slug}`,
            steps: [
              {
                text: prompt.content,
                name: "AI Prompt Instructions",
              },
            ],
            totalTime: "PT5M",
            author: "One80Labs",
          }),
          promptSchema as any,
          localBusinessSchema as any,
        ]}
        breadcrumbs={[
          { name: "Home", url: "https://one80labs.com" },
          {
            name: "Prompt Library",
            url: "https://one80labs.com/prompt-library",
          },
          { name: prompt.title, url: `https://one80labs.com/prompt/${slug}` },
        ]}
        keywords={[
          prompt.title,
          "AI prompt",
          "ChatGPT prompt",
          "Gemini prompt",
          "prompt engineering",
          "Omaha AI",
          "Midwest AI",
          "business AI",
          ...((prompt as any).prompt_tags_map?.map(
            (tag: any) => tag.prompt_tags.name,
          ) || []),
        ]}
      />
      <Navigation />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate("/prompt-library")}
          className="inline-flex items-center text-[#F98B3D] hover:text-[#e07a2c] mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Prompt Library
        </button>

        {/* Prompt Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {prompt.title}
          </h1>
          <p className="text-lg text-gray-600 mb-4">{prompt.description}</p>

          {/* Category and Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {prompt.prompt_categories && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {prompt.prompt_categories.name}
              </span>
            )}
            {(prompt as any).prompt_tags_map?.map((tagMap: any) => (
              <span
                key={tagMap.prompt_tags.id}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
              >
                <Tag className="h-3 w-3 mr-1" />
                {tagMap.prompt_tags.name}
              </span>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => handleCopy(prompt.content)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
            >
              {copiedText === prompt.content ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Prompt
                </>
              )}
            </button>

            {prompt.download_url && (
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                {prompt.download_button_text || "Download"}
              </button>
            )}
          </div>
        </div>

        {/* Video Section */}
        {prompt.youtube_video_id && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Related Video
            </h2>
            <div className="aspect-video">
              <YouTubeEmbed
                videoId={prompt.youtube_video_id}
                title={prompt.title}
                lazyLoad={false}
              />
            </div>
          </div>
        )}

        {/* Prompt Content */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Prompt Content
          </h2>
          <div className="bg-white rounded border p-4 font-mono text-sm text-gray-800 whitespace-pre-wrap">
            {prompt.content}
          </div>
        </div>

        {/* Related Prompts Section */}
        <div className="border-t pt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            More AI Prompts
          </h2>
          <p className="text-gray-600 mb-4">
            Explore our full collection of AI prompts designed for businesses in
            Omaha and the Midwest.
          </p>
          <button
            onClick={() => navigate("/prompt-library")}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
          >
            Browse All Prompts
          </button>
        </div>
      </main>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />

      {/* Email Capture Modal for Downloads */}
      {showDownloadModal && prompt.download_url && (
        <EmailCaptureModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          downloadUrl={prompt.download_url}
          promptId={prompt.id}
          source={`Prompt Detail Download: ${prompt.title}`}
          title="Download Resource"
          description="Please enter your name and email to access this resource. We respect your privacy and will never share your information."
        />
      )}

      <Footer />
    </div>
  );
}
